import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import BubbleCard from '@/components/BubbleCard';
import FounderBadge from '@/components/FounderBadge';
import { Trophy, Crown, Lightning, Users, HandHeart } from '@phosphor-icons/react';

const TAB = [
  { key: 'users', label: 'Founders', icon: Crown },
  { key: 'raised', label: 'Top Givers', icon: HandHeart },
  { key: 'bubbles', label: 'Bubbles', icon: Lightning },
  { key: 'tribes', label: 'Tribes', icon: Users },
];

const Leaderboards = () => {
  const [tab, setTab] = useState('users');
  const [data, setData] = useState({ users: [], givers: [], bubbles: [], tribes: [] });

  useEffect(() => {
    Promise.all([
      api.get('/leaderboard/users?metric=xp'),
      api.get('/leaderboard/users?metric=raised'),
      api.get('/leaderboard/bubbles?metric=velocity'),
      api.get('/leaderboard/tribes'),
    ]).then(([u, g, b, t]) => setData({
      users: u.data.users,
      givers: g.data.users.filter((x) => (x.total_raised_usd || 0) > 0),
      bubbles: b.data.bubbles,
      tribes: t.data.tribes,
    }));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="text-center mb-8">
        <Trophy size={36} className="text-bx-gold mx-auto" weight="duotone" />
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Global Leaderboards</h1>
        <p className="text-white/60 mt-1">The relentless. The generous. The rising.</p>
      </div>

      <div className="flex justify-center gap-2 mb-8">
        {TAB.map((t) => (
          <button
            key={t.key}
            data-testid={`lb-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-5 py-2.5 rounded-full text-sm font-bold flex items-center gap-2 transition ${
              tab === t.key ? 'bg-bx-cyan text-black' : 'glass-hi text-white/70 hover:bg-white/10'
            }`}
          >
            <t.icon size={14} weight="bold" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'users' && (
        <div className="space-y-2">
          {data.users.map((u, i) => (
            <div key={u.id} data-testid={`lb-user-${i}`} className="glass rounded-xl p-4 flex items-center gap-4">
              <div className="font-display text-2xl font-black w-10 text-center">
                {i === 0 ? '🏆' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
              </div>
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-bx-cyan to-bx-magenta grid place-items-center font-display font-black">
                {u.username?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold">@{u.username}<FounderBadge user={u} username={u.username} className="ml-1" /></div>
                <div className="text-xs text-white/65">L{u.level} · Founder #{u.founder_number || '—'}{(u.total_raised_usd > 0) && <> · <span className="text-bx-magenta">${u.total_raised_usd.toFixed(2)} raised</span></>}</div>
              </div>
              <div className="text-right">
                <div className="text-bx-volt font-black font-display text-xl">{u.xp.toLocaleString()}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">XP</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'raised' && (
        <>
          <div className="glass rounded-2xl p-5 mb-4 text-center">
            <HandHeart size={24} weight="duotone" className="text-bx-magenta mx-auto" />
            <div className="font-display text-2xl font-black mt-2">
              <span className="shimmer-text">${data.givers.reduce((a, g) => a + (g.total_raised_usd || 0), 0).toFixed(2)}</span>
              <span className="text-white/55 text-base ml-2">raised by this community</span>
            </div>
          </div>
          {data.givers.length === 0 ? (
            <div className="text-center text-white/65 py-10 text-sm">No givers yet. Be the first to donate via <Link to="/charity" className="text-bx-cyan font-bold">/charity</Link> or pick a cause on a <Link to="/drops" className="text-bx-cyan font-bold">Drop</Link>.</div>
          ) : (
            <div className="space-y-2">
              {data.givers.map((u, i) => (
                <div key={u.id} data-testid={`lb-giver-${i}`} className="glass rounded-xl p-4 flex items-center gap-4">
                  <div className="font-display text-2xl font-black w-10 text-center">
                    {i === 0 ? '👑' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                  </div>
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-bx-magenta to-bx-gold grid place-items-center font-display font-black">
                    {u.username?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold">@{u.username}<FounderBadge user={u} username={u.username} className="ml-1" /></div>
                    <div className="text-xs text-white/65">L{u.level} · Founder #{u.founder_number || '—'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-bx-magenta font-black font-display text-xl">${(u.total_raised_usd || 0).toFixed(2)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/60">raised</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'bubbles' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {data.bubbles.map((b, i) => (
            <Link to={`/bubble/${b.id}`} key={b.id} className="glass rounded-2xl p-4" data-testid={`lb-bubble-${i}`}>
              <div className="text-center font-display font-black text-bx-cyan mb-2">#{i + 1}</div>
              <BubbleCard bubble={b} size="sm" />
              <div className="mt-2 text-center text-[10px] uppercase tracking-widest text-white/65">
                v{b.velocity} · ⚡{b.energy}
              </div>
            </Link>
          ))}
        </div>
      )}

      {tab === 'tribes' && (
        <div className="space-y-2">
          {data.tribes.map((t, i) => (
            <Link to={`/tribe/${t.id}`} key={t.id} className="glass rounded-xl p-4 flex items-center gap-4 hover:bg-white/5 transition" data-testid={`lb-tribe-${i}`}>
              <div className="font-display text-2xl font-black w-10 text-center">#{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold">{t.name}</div>
                <div className="text-xs text-white/65">{t.tag} · {t.member_count || 0} members</div>
              </div>
              <div className="text-right">
                <div className="text-bx-volt font-black font-display text-xl">{t.energy}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">energy</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Leaderboards;
